import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LanguageContext from "../../helper/LanguageContext";
import { $user } from "../../helper/UserFactory";
import {
  buildVideoPosterUrl,
  buildVideoSrc,
  getVideoMimeType,
} from "../../utils/chatVideo";
import useMediaReveal from "../../hooks/useMediaReveal";

function nativeDisplaySize(videoEl) {
  const nw = videoEl?.videoWidth || 640;
  const nh = videoEl?.videoHeight || 360;
  const maxW = Math.max(160, window.innerWidth - 48);
  const maxH = Math.max(120, window.innerHeight - 48);
  const scale = Math.min(1, maxW / nw, maxH / nh);
  return {
    width: Math.round(nw * scale),
    height: Math.round(nh * scale),
  };
}

function exitAnyFullscreen(el) {
  try {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen();
    } else if (el?.webkitDisplayingFullscreen && el.webkitExitFullscreen) {
      el.webkitExitFullscreen();
    }
  } catch (_) { /* ignore */ }
}

const RING_R = 26;
const RING_C = 2 * Math.PI * RING_R;

function UploadOverlay({ progress, processing, strings }) {
  const pct = Math.min(100, Math.max(0, Math.round(Number(progress) || 0)));
  const dash = (pct / 100) * RING_C;
  return (
    <div className="chat-video-upload-overlay">
      <div className="chat-video-upload-card">
        <div
          className="chat-video-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <svg className="chat-video-progress-ring" viewBox="0 0 72 72" aria-hidden="true">
            <circle className="chat-video-progress-track" cx="36" cy="36" r={RING_R} />
            <circle
              className="chat-video-progress-value"
              cx="36"
              cy="36"
              r={RING_R}
              strokeDasharray={`${dash} ${RING_C}`}
            />
          </svg>
          <div className="chat-video-progress-pct">{`${pct}%`}</div>
        </div>
        <div className="chat-video-progress-label">
          {processing
            ? (strings.video_processing || "Compressing video…")
            : (strings.video_uploading || "Uploading video…")}
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg className="chat-video-play" viewBox="0 0 72 72" aria-hidden="true">
      <circle cx="36" cy="36" r="34" fill="rgba(0,0,0,0.45)" />
      <circle cx="36" cy="36" r="34" fill="none" stroke="#fff" strokeWidth="3" />
      <path d="M30 22.5v27L51 36z" fill="#fff" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 3a1 1 0 0 1 1 1v8.17l2.59-2.58a1 1 0 1 1 1.41 1.42l-4.3 4.29a1 1 0 0 1-1.4 0l-4.3-4.3a1 1 0 1 1 1.41-1.41L11 12.17V4a1 1 0 0 1 1-1zm-7 14a1 1 0 0 1 1 1v1h12v-1a1 1 0 1 1 2 0v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1z"
      />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4 9V4h5v2H6v3H4zm10-5h5v5h-2V6h-3V4zM4 15h2v3h3v2H4v-5zm14 3h-3v2h5v-5h-2v3z"
      />
    </svg>
  );
}

function LocalVideoThumb({ src }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const showFrame = () => {
      try {
        if (el.currentTime < 0.05) el.currentTime = 0.15;
      } catch (_) { /* ignore seek errors on some codecs */ }
    };
    if (el.readyState >= 1) showFrame();
    else el.addEventListener("loadedmetadata", showFrame);
    return () => el.removeEventListener("loadedmetadata", showFrame);
  }, [src]);
  return (
    <video
      ref={ref}
      className="chat-video-thumb"
      src={src}
      muted
      playsInline
      preload="metadata"
    />
  );
}

export default function ChatVideoBubble({
  fileUrl,
  thumbnailUrl,
  local = false,
  attachedFileName,
  compact = false,
  uploading = false,
  progress = null,
  processing = false,
  serverFileUrl = null,
  hideToolbar = false,
}) {
  const { strings } = useContext(LanguageContext);
  const [posterFailed, setPosterFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [nativeView, setNativeView] = useState(null);
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const openNativeViewRef = useRef(() => {});
  const effectiveUrl = serverFileUrl || fileUrl;
  const effectiveLocal = local && !serverFileUrl;
  const src = buildVideoSrc(effectiveLocal, effectiveUrl, attachedFileName);
  const poster = buildVideoPosterUrl(effectiveLocal, thumbnailUrl, effectiveUrl);
  const mime = getVideoMimeType(effectiveUrl);
  const showPoster = poster && !posterFailed && !String(poster).startsWith("blob:");
  const useLocalVideoThumb = effectiveLocal && src && (src.startsWith("blob:") || src.startsWith("data:"));
  const showUpload = uploading;
  const downloadName = attachedFileName || "video.mp4";
  const downloadLabel = strings.download_selected || "Download";
  // The player has no poster load event, so the poster is preloaded to time
  // its reveal; without a poster the first frame (loadeddata) does it.
  const thumbReveal = useMediaReveal(showPoster ? poster : "");
  const playerReveal = useMediaReveal(showPoster ? poster : src, { probe: showPoster });

  const openNativeView = (fromEl) => {
    const el = fromEl || videoRef.current;
    if (!el) return;
    const size = nativeDisplaySize(el);
    setNativeView({
      time: el.currentTime || 0,
      playing: !el.paused,
      ...size,
    });
    el.pause();
    exitAnyFullscreen(el);
  };
  openNativeViewRef.current = openNativeView;

  const closeNativeView = () => {
    const overlay = overlayRef.current;
    const bubble = videoRef.current;
    if (bubble && overlay) {
      try {
        bubble.currentTime = overlay.currentTime || 0;
        if (!overlay.paused) {
          bubble.play().catch(() => {});
        }
      } catch (_) { /* ignore */ }
    }
    setNativeView(null);
  };

  const bindPlayer = (el) => {
    videoRef.current = el;
    playerReveal.ref.current = el;
    if (!el) return;
    const hijack = () => {
      openNativeViewRef.current(el);
      return Promise.resolve();
    };
    el.requestFullscreen = hijack;
    el.webkitRequestFullscreen = hijack;
    el.webkitEnterFullscreen = hijack;
    el.mozRequestFullScreen = hijack;
    el.msRequestFullscreen = hijack;
  };

  useEffect(() => {
    const intercept = () => {
      const el = videoRef.current;
      if (!el) return;
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      const isOurs = fsEl && (fsEl === el || el.contains(fsEl) || fsEl.contains?.(el));
      if (!isOurs && !el.webkitDisplayingFullscreen) return;
      openNativeViewRef.current(el);
    };
    document.addEventListener("fullscreenchange", intercept);
    document.addEventListener("webkitfullscreenchange", intercept);
    return () => {
      document.removeEventListener("fullscreenchange", intercept);
      document.removeEventListener("webkitfullscreenchange", intercept);
    };
  }, []);

  useEffect(() => {
    if (!nativeView) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeNativeView();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [nativeView]);

  const thumb = useLocalVideoThumb ? (
    <LocalVideoThumb src={src} />
  ) : showPoster ? (
    <img
      ref={thumbReveal.ref}
      className={thumbReveal.className}
      src={poster}
      alt=""
      onLoad={thumbReveal.done}
      onError={() => {
        thumbReveal.fail();
        setPosterFailed(true);
      }}
    />
  ) : (
    <div className="chat-video-placeholder" />
  );

  if (compact) {
    return (
      <div className="chat-video-bubble chat-video-bubble-compact">
        {thumb}
        <PlayIcon />
      </div>
    );
  }

  if (showUpload) {
    return (
      <div className="chat-video-bubble is-uploading" aria-busy="true">
        {thumb}
        <UploadOverlay
          progress={progress}
          processing={processing}
          strings={strings || {}}
        />
      </div>
    );
  }

  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!src || downloading) return;
    setDownloading(true);
    try {
      if (src.startsWith("blob:") || src.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = src;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      const token = $user.getToken && $user.getToken();
      const res = await fetch(src, {
        headers: token ? { Authorization: `jwt ${token}` } : {},
      });
      if (!res.ok) throw new Error("download");
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    } catch (_) {
      window.open(src, "_blank");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="chat-video-player-wrap">
        <video
          ref={bindPlayer}
          className={`chat-video-player ${playerReveal.className}`}
          onLoadedData={playerReveal.done}
          controls
          controlsList="nofullscreen noremoteplayback"
          disablePictureInPicture
          playsInline
          preload="metadata"
          poster={showPoster ? poster : undefined}
        >
          <source src={src} type={mime} />
        </video>
        {!hideToolbar && (
          <>
            <button
              type="button"
              className="chat-video-expand"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openNativeView(videoRef.current);
              }}
              title={strings.full_screen || "Full size"}
              aria-label={strings.full_screen || "Full size"}
            >
              <ExpandIcon />
            </button>
            <button
              type="button"
              className="chat-video-download"
              onClick={handleDownload}
              disabled={downloading}
              title={downloadLabel}
              aria-label={downloadLabel}
            >
              <DownloadIcon />
            </button>
          </>
        )}
      </div>
      {nativeView &&
        createPortal(
          <div
            className="chat-video-native-overlay"
            onClick={closeNativeView}
          >
            <button
              type="button"
              className="chat-video-native-close"
              onClick={(e) => {
                e.stopPropagation();
                closeNativeView();
              }}
              title={strings.close || "Close"}
              aria-label={strings.close || "Close"}
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"
                />
              </svg>
            </button>
            <video
              ref={overlayRef}
              className="chat-video-native-player"
              controls
              playsInline
              autoPlay={nativeView.playing}
              controlsList="nofullscreen"
              disablePictureInPicture
              poster={showPoster ? poster : undefined}
              style={{
                width: nativeView.width,
                height: nativeView.height,
              }}
              onClick={(e) => e.stopPropagation()}
              onLoadedMetadata={(e) => {
                try {
                  e.currentTarget.currentTime = nativeView.time || 0;
                  if (nativeView.playing) {
                    e.currentTarget.play().catch(() => {});
                  }
                } catch (_) { /* ignore */ }
              }}
            >
              <source src={src} type={mime} />
            </video>
          </div>,
          document.body
        )}
    </>
  );
}
